"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { ArrowUpRight, Plus, Minus } from "@phosphor-icons/react";

import { Link } from "@/navigation";

const NAMESPACE = "new-sla-stereolithography";

const HERO_IMAGE = "/image/new-version/more/SLA/stereolithography-sla-3d-printing-material-options.webp";

const ITEMS = Array.from({ length: 10 }, (_, i) => i + 1);

export default function MaterialOptions() {
	const t = useTranslations(NAMESPACE);
	const [openIndex, setOpenIndex] = useState(0);

	const toggle = (index: number) => {
		setOpenIndex(openIndex === index ? -1 : index);
	};

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="flex flex-col gap-8 lg:flex-row lg:gap-12 xl:gap-16">
					{/* Left: image */}
					<div className="w-full lg:w-[380px] lg:shrink-0 xl:w-[420px]">
						<div className="relative aspect-[5/4] w-full overflow-hidden">
							<Image
								src={HERO_IMAGE}
								alt={t("materialOptionsImageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 420px"
							/>
						</div>
					</div>

					{/* Right: title + accordion */}
					<div className="flex-1">
						<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
							{t("materialOptionsTitle")}
						</h2>

						<div className="divide-y divide-gray-200 border-t border-gray-200">
							{ITEMS.map((n) => {
								const isOpen = openIndex === n - 1;
								const title = t(`materialOptionsItem${n}Title` as any);
								const subtitle = t(`materialOptionsItem${n}Subtitle` as any);

								return (
									<div key={n}>
										<button
											onClick={() => toggle(n - 1)}
											className="flex w-full items-center justify-between py-4 text-left"
										>
											<div className="flex items-baseline gap-2">
												<span className="text-base font-bold text-black sm:text-lg">{title}</span>
												{subtitle && <span className="text-sm text-gray-500">({subtitle})</span>}
											</div>
											{isOpen ? (
												<Minus size={22} weight="bold" className="shrink-0 text-gray-800 transition-colors" />
											) : (
												<Plus
													size={22}
													weight="bold"
													className="shrink-0 text-themePrimary400 transition-colors"
												/>
											)}
										</button>

										{isOpen && (
											<div className="pb-6 pl-0 pr-8">
												<p className="mb-4 text-sm leading-relaxed text-black md:text-base">
													{t(`materialOptionsItem${n}Desc` as any)}
												</p>

												<h4 className="mb-2 text-base font-bold text-black">
													{t("materialOptionsKeyBenefits")}
												</h4>
												<ul className="mb-4 space-y-1.5">
													<li className="flex items-start gap-2 text-sm text-black md:text-base">
														<span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-themePrimary400" />
														{t(`materialOptionsItem${n}Benefit1` as any)}
													</li>
													<li className="flex items-start gap-2 text-sm text-black md:text-base">
														<span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-themePrimary400" />
														{t(`materialOptionsItem${n}Benefit2` as any)}
													</li>
												</ul>

												<Link
													href="/contact-us"
													className="inline-flex items-center gap-1 text-sm font-medium text-themePrimary400 transition-opacity hover:opacity-80"
												>
													{t("materialOptionsContactUs")}
													<ArrowUpRight size={16} weight="bold" />
												</Link>
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
